import type { ReactNode } from 'react';
import { useRef } from 'react';

type TagItem<T extends string> = {
  id: T;
  label: ReactNode;
  count?: number;
  hasAlertDot?: boolean;
};

interface TagBarProps<T extends string> {
  items: Array<TagItem<T>>;
  activeId: T;
  onChange: (id: T) => void;
  className?: string;
  innerClassName?: string;
  activeClassName?: string;
  inactiveClassName?: string;
  itemClassName?: string;
  showCounts?: boolean;
  centerOnClick?: boolean;
}

const TagBar = <T extends string>({
  items,
  activeId,
  onChange,
  className = '',
  innerClassName = '',
  activeClassName = 'text-primary border-b-4 border-primary',
  inactiveClassName = 'text-gray-600 hover:text-primary',
  itemClassName = 'px-8 py-4 font-semibold whitespace-nowrap transition-all',
  showCounts = false,
  centerOnClick = true,
}: TagBarProps<T>) => {
  const barRef = useRef<HTMLDivElement>(null);
  const buttonRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const scrollToTag = (index: number) => {
    const tagBar = barRef.current;
    const targetButton = buttonRefs.current[index];

    if (!tagBar || !targetButton || !centerOnClick) {
      return;
    }

    const targetLeft = targetButton.offsetLeft;
    const targetWidth = targetButton.offsetWidth;
    const desiredScrollLeft = targetLeft - (tagBar.clientWidth - targetWidth) / 2;
    const maxScrollLeft = Math.max(0, tagBar.scrollWidth - tagBar.clientWidth);
    const nextScrollLeft = Math.max(0, Math.min(desiredScrollLeft, maxScrollLeft));

    tagBar.scrollTo({
      left: nextScrollLeft,
      behavior: 'smooth',
    });
  };

  return (
    <div className={className}>
      <div ref={barRef} className={innerClassName}>
        {items.map((item, index) => (
          <button
            key={item.id}
            ref={el => {
              buttonRefs.current[index] = el;
            }}
            onClick={() => {
              onChange(item.id);
              scrollToTag(index);
            }}
            className={`${itemClassName} ${activeId === item.id ? activeClassName : inactiveClassName}`}
          >
            <span className="inline-flex items-center gap-2">
              <span>{item.label}</span>
              {item.hasAlertDot && (
                <span
                  aria-label="Needs attention"
                  className="inline-block h-2.5 w-2.5 rounded-full bg-red-500"
                />
              )}
            </span>
            {showCounts && (
              <span className="ml-2 inline-block bg-gray-100 text-gray-700 rounded-full px-2 py-0.5 text-xs font-semibold">
                {item.count ?? 0}
              </span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

export default TagBar;
